import { PRODUCTS, productLines, stationIsLive } from './constants'
import { tankFillFraction } from '../../utils/dataHelpers'

/** One thin bar per tank, filled to its last measured level, coloured by product. */
function MiniBars({ station, levels }) {
  const tanks = productLines(station).flatMap((l) => l.tanks.map((t) => ({ ...t, product: l.product })))
  if (tanks.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 px-2.5 py-2 text-[12px] text-gray-400">
        No products yet — click in to set up the first one.
      </p>
    )
  }
  return (
    <div className="flex h-[30px] items-end gap-1">
      {tanks.map((t) => {
        const fill = tankFillFraction(t, levels)
        const pct = fill == null ? 0 : Math.round(fill * 100)
        return (
          <span
            key={t.id}
            title={`Tank ${t.tankNumber} · ${t.product} — ${fill == null ? 'no readings yet' : pct + '%'}`}
            className="h-full w-[7px] overflow-hidden rounded-[3px] bg-gray-100"
          >
            <span
              className="block w-full rounded-t-[3px]"
              style={{ height: `${pct}%`, marginTop: `${100 - pct}%`, background: PRODUCTS[t.product]?.color || '#94a3b8' }}
            />
          </span>
        )
      })}
    </div>
  )
}

function StationCard({ station, levels, onOpen }) {
  const lines = productLines(station)
  const tanks = lines.reduce((n, l) => n + l.tanks.length, 0)
  const pumps = lines.reduce((n, l) => n + l.pumps.length, 0)
  const live = stationIsLive(station)

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col gap-3 rounded-[22px] border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2932b]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[15.5px] font-bold text-gray-900">{station.name}</p>
          <p className="truncate text-[12.5px] text-gray-400">{station.location || 'Unset location'}</p>
        </div>
        <span
          className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${
            live ? 'bg-[#0f9d63]/10 text-[#0f9d63]' : 'bg-[#b8791f]/10 text-[#b8791f]'
          }`}
        >
          {live ? 'Live' : 'Needs setup'}
        </span>
      </div>

      <MiniBars station={station} levels={levels} />

      <div className="mt-auto flex items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          {[`${lines.length} product${lines.length === 1 ? '' : 's'}`, `${tanks} tank${tanks === 1 ? '' : 's'}`, `${pumps} pump${pumps === 1 ? '' : 's'}`].map((c) => (
            <span key={c} className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-600">{c}</span>
          ))}
        </div>
        <span className="text-[15px] text-gray-300" aria-hidden="true">→</span>
      </div>
    </button>
  )
}

export default function OrgView({ orgName, stations, levels, onOpenStation, onAddStation }) {
  const tanks = stations.reduce((n, s) => n + (s.tanks?.length || 0), 0)
  const pumps = stations.reduce((n, s) => n + (s.pumps?.length || 0), 0)

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-5">
        <div>
          <h1 className="text-[clamp(1.4rem,1.1rem+1vw,1.85rem)] font-bold tracking-[-.01em] text-gray-900">{orgName}</h1>
          <p className="mt-1.5 text-[13.5px] text-gray-500">
            {stations.length} station{stations.length === 1 ? '' : 's'} · {tanks} tank{tanks === 1 ? '' : 's'} · {pumps} pump{pumps === 1 ? '' : 's'}, all in one place.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddStation}
          className="rounded-lg bg-[#c97a1a] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#b06914]"
        >
          + Add station
        </button>
      </div>

      <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]">
        {stations.map((s) => (
          <StationCard key={s.id} station={s} levels={levels} onOpen={() => onOpenStation(s.id)} />
        ))}
        <button
          type="button"
          onClick={onAddStation}
          className="flex min-h-[150px] flex-col items-center justify-center gap-2 rounded-[22px] border-[1.5px] border-dashed border-gray-300 text-gray-400 transition hover:border-[#e2932b] hover:bg-[#e2932b]/10 hover:text-[#e2932b]"
        >
          <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-[1.5px] border-current text-lg leading-none">+</span>
          <span className="text-[13px] font-semibold">Add a station</span>
        </button>
      </div>
    </div>
  )
}
